/**
 * Raw GPS Data Service for FlowRide
 * Provides raw GPS position and direction data from the device
 * Implements event-based communication with last value caching
 */

import { createGenevaRawGpsData, GENEVA_DEFAULT_POSITION } from "../../tools/GenevaDefaults";
import { GpsData, NavigationEventType, RawGpsData, type NavigationEvent } from "../../types/Navigation";
import { gpsPermissionService, type GpsPermissionState } from "./GpsPermissionService";

/**
 * Event listener callback type
 */
type GpsEventListener = ( data: RawGpsData ) => void;


/**
 * Service that manages raw GPS data from the device
 */
export class RawGpsDataService {

    /** Singleton instance */
    private static instance: RawGpsDataService | null = null;

    /** Last GPS data received */
    private lastValue: RawGpsData = createGenevaRawGpsData();

    /** Event listeners */
    private listeners: Set<GpsEventListener> = new Set();

    /** Geolocation watch ID */
    private watchId: number | null = null;

    /** Set last GPS data callback */
    private setLastGpsData: ( lastGpsData: RawGpsData ) => void = () => {
    };

    /** Whether the service is currently active */
    private active: boolean = false;

    /** Minimum time between updates in milliseconds */
    private readonly MIN_UPDATE_INTERVAL = 100;

    /** Last update timestamp */
    private lastUpdateTime: number = 0;

    /**
     * Private constructor for singleton pattern
     */
    private constructor() {
        // Private constructor
    }

    /**
     * Get singleton instance
     * @returns Service instance
     */
    public static getInstance(): RawGpsDataService {
        if ( RawGpsDataService.instance === null ) {
            RawGpsDataService.instance = new RawGpsDataService();
        }

        return RawGpsDataService.instance;
    }

    /**
     * Get the last GPS data value
     * @returns Last GPS data or null if no data available
     */
    public getLastValue(): RawGpsData {
        return this.lastValue;
    }

    public initLastValue( lastGpsData: GpsData ): void {
        this.lastValue = {
            ...lastGpsData,
            "accuracy":         GENEVA_DEFAULT_POSITION.accuracy,
            "altitudeAccuracy": 50,
            "timestamp":        Date.now(),
        } as RawGpsData;
    }

    public async start( setLastGpsData: ( lastGpsData: RawGpsData ) => void ): Promise<void> {
        if ( this.isActive() ) {
            console.warn( "[RawGpsDataService] Already active" );

            return;
        }

        if ( navigator.geolocation === undefined ) {
            console.error( "[RawGpsDataService] Geolocation not supported" );

            return;
        }

        this.setLastGpsData = setLastGpsData;

        // Check permission first using GpsPermissionService
        const hasPermission = await gpsPermissionService.requestPermission();
        if ( !hasPermission ) {
            console.error( "[RawGpsDataService] Cannot start GPS tracking: permission denied" );

            return;
        }

        this.active = true;

        /*
         * Plus d'émission de position au démarrage - MapComponent gère ça directement
         * console.log( "[RawGpsDataService] Started GPS tracking - MapComponent will handle initial positioning" );
         */

        this.watchId = navigator.geolocation.watchPosition(
            this.handlePosition.bind( this ),
            this.handleError.bind( this ),
            {
                "enableHighAccuracy": true,
                "maximumAge":         0,
                "timeout":            10000, // Increased timeout to 10 seconds
            },
        );

        // console.log( "[RawGpsDataService] Started GPS tracking with 10s timeout" );
    }

    /**
     * Pause GPS tracking by removing the watch but letting service on
     */
    public pause() {
        if ( this.watchId !== null ) {
            navigator.geolocation.clearWatch( this.watchId );
            this.watchId = null;
        }
    }

    /**
     * Resume GPS tracking by getting current position and re-setting the watch
     */
    public resume() {
        this.getCurrentPosition();

        this.watchId = navigator.geolocation.watchPosition(
            this.handlePosition.bind( this ),
            this.handleError.bind( this ),
            {
                "enableHighAccuracy": true,
                "maximumAge":         0,
                "timeout":            2000, // Increased timeout to 10 seconds
            },
        );
    }

    /**
     * Force navigator to update position to current position
     */
    private getCurrentPosition(): void {
        navigator.geolocation.getCurrentPosition(
            this.handlePosition.bind( this ),
            this.handleError.bind( this ),
            {
                "enableHighAccuracy": true,
                "maximumAge":         0,
                "timeout":            2000, // 1s to refresh
            },
        );
    }

    /**
     * Stop GPS tracking
     */
    public stop(): void {
        if ( this.watchId !== null ) {
            navigator.geolocation.clearWatch( this.watchId );
            this.watchId = null;
        }
        this.active = false;

        // console.log( "[RawGpsDataService] Stopped GPS tracking" );
    }

    /**
     * Subscribe to GPS updates
     * @param listener - Callback function
     * @returns Unsubscribe function
     */
    public subscribe( listener: GpsEventListener ): () => void {
        this.listeners.add( listener );

        return () => {
            this.listeners.delete( listener );
        };
    }

    /**
     * Handle successful GPS position
     * @param position - Geolocation position
     */
    private handlePosition( position: GeolocationPosition ): void {
        const now = Date.now();

        // Throttle updates to prevent excessive processing
        if ( now - this.lastUpdateTime < this.MIN_UPDATE_INTERVAL ) {
            return;
        }

        this.lastUpdateTime = now;

        const rawData: RawGpsData = {
            "latitude":         position.coords.latitude,
            "longitude":        position.coords.longitude,
            "accuracy":         position.coords.accuracy,
            "altitude":         position.coords.altitude ?? 0,
            "altitudeAccuracy": position.coords.altitudeAccuracy ?? 0,
            "heading":          position.coords.heading ?? 0,
            "speed":            position.coords.speed ?? 0,
            "timestamp":        position.timestamp,
        };

        // Store last value
        this.lastValue = rawData;

        // Save position for next startup
        this.savePosition( rawData );

        // Emit to all listeners
        this.emit( rawData );
    }

    /**
     * Save position for next startup
     * @param position - GPS position to save
     */
    private savePosition( position: RawGpsData ): void {
        this.setLastGpsData( position );
    }

    /**
     * Handle GPS error
     * @param error - Geolocation position error
     */
    private handleError( error: GeolocationPositionError ): void {
        switch ( error.code ) {
            case GeolocationPositionError.PERMISSION_DENIED: {
                console.error( "[RawGpsDataService] GPS permission denied by user" );
                gpsPermissionService.setPermissionState( "denied" );

                // Stop the service if permission is denied
                this.stop();

                break;
            }

            case GeolocationPositionError.POSITION_UNAVAILABLE: {
                console.warn( "[RawGpsDataService] GPS position unavailable:", error.message );

                break;
            }

            case GeolocationPositionError.TIMEOUT: {
                console.warn( "[RawGpsDataService] GPS timeout:", error.message );

                break;
            }

            default: {
                console.error( "[RawGpsDataService] GPS error:", {
                    "code":    error.code,
                    "message": error.message,
                } );

                break;
            }
        }
    }

    /**
     * Emit GPS data to all listeners
     * @param data - Raw GPS data
     */
    private emit( data: RawGpsData ): void {
        const event: NavigationEvent<RawGpsData> = {
            "type":      NavigationEventType.RAW_GPS_UPDATE,
            data,
            "timestamp": data.timestamp,
        };

        console.log( "[RawGpsDataService] Emitting GPS data to listeners", {
            "listenerCount": this.listeners.size,
            "latitude":      data.latitude,
            "longitude":     data.longitude,
            "speed":         data.speed,
        } );

        if ( this.listeners.size === 0 ) {
            console.warn( "[RawGpsDataService] No listeners registered! Data will not be processed." );
        }

        Array.from( this.listeners ).forEach( ( listener, index ) => {
            console.log( `[RawGpsDataService] Calling listener ${ index + 1 }/${ this.listeners.size }` );
            try {
                listener( event.data );
            } catch( error ) {
                console.error( "[RawGpsDataService] Error in listener:", {
                    "error":         error,
                    "errorMessage":  error instanceof Error ? error.message : "Unknown error",
                    "errorStack":    error instanceof Error ? error.stack : "No stack trace",
                    "listenerCount": this.listeners.size,
                    "gpsData":       {
                        "lat":      data.latitude.toFixed( 6 ),
                        "lng":      data.longitude.toFixed( 6 ),
                        "accuracy": data.accuracy?.toFixed( 1 ) + "m",
                        "speed":
                            data.speed !== null ? ( data.speed * 3.6 ).toFixed( 1 ) + " km/h" : "null",
                        "heading": data.heading !== null ? data.heading.toFixed( 1 ) + "°" : "null",
                    },
                } );
            }
        } );
    }

    /**
     * Check if service is active
     * @returns True if active
     */
    public isActive(): boolean {
        return this.active;
    }

    /**
     * Get number of active listeners
     * @returns Number of listeners
     */
    public getListenerCount(): number {
        return this.listeners.size;
    }

    /**
     * Check if geolocation permission is granted
     * @returns True if permission is granted
     */
    public isPermissionGranted(): boolean {
        return gpsPermissionService.isPermissionGranted();
    }

    /**
     * Get current permission state
     * @returns Current permission state
     */
    public getPermissionState(): GpsPermissionState {
        return gpsPermissionService.getPermissionState();
    }

    /**
     * Subscribe to permission state changes
     * @param listener - Callback function that receives permission state
     * @returns Unsubscribe function
     */
    public subscribeToPermissionState( listener: ( state: GpsPermissionState ) => void ): () => void {
        return gpsPermissionService.subscribeToPermissionState( listener );
    }

    /**
     * Inject GPS data (for replay mode)
     * @param data - GPS data to inject
     */
    public injectData( data: RawGpsData ): void {
        console.log( "[RawGpsDataService] Injecting GPS data", {
            "listeners": this.listeners.size,
            "latitude":  data.latitude,
            "longitude": data.longitude,
            "timestamp": data.timestamp,
            "speed":     data.speed,
            "heading":   data.heading,
        } );

        // Store last value
        this.lastValue = data;

        // Save position for next startup
        this.savePosition( data );

        // Emit to all listeners
        this.emit( data );
    }

}

/**
 * Export singleton instance for convenience
 */
export const rawGpsDataService = RawGpsDataService.getInstance();
